import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CardData } from '../data/types/CardData';
import { CreditCard } from 'data/shared/CreditCard';
import {
  SetStateAction,
  useCallback,
  useState,
  Dispatch,
  useEffect,
  useRef,
} from 'react';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { CreditCardDataFiltered } from 'data/shared/CreditCardData';
import { CreditCardImage } from './design-system/CreditCardImage';
import { useScreenSize } from '../hooks/useScreenSize';
import { SegmentedControl } from './design-system/SegmentedControl';
import {
  matchCategories,
  getCardsForCategory,
  CategoryMatch,
} from '../utils/categorySearch';
import { flattenRewardsEarnRates } from 'data/shared/utils/flattenRewardsEarnRates';
import { formatEarnRate } from 'data/shared/utils/formatEarnRate';
import { formatSpendCategory } from 'data/shared/utils/formatSpendCategory';
import { RequestCardModal } from './RequestCardModal';

// Normalize text for search — always strips trademark symbols and collapses whitespace.
// When stripPunctuation is true (used for targets), also strips ., +, -, ' so
// a query without those chars still matches. The query itself keeps them so that
// typing "cash+" is more specific than "cash".
const normalizeForSearch = (text: string, stripPunctuation = false): string => {
  let result = text.toLowerCase().replace(/[®™©℠]/g, '');
  if (stripPunctuation) {
    result = result.replace(/[.+\-'']/g, '');
  }
  return result.replace(/\s+/g, ' ').trim();
};

// Check if all search tokens appear in the target string (order-independent).
// First tries matching against the punctuation-stripped target (fuzzy).
// If any token contains a special char, also tries the raw target so that
// "cash+" only matches names that actually contain "cash+".
const fuzzyTokenMatch = (target: string, query: string): boolean => {
  const strippedTarget = normalizeForSearch(target, true);
  const rawTarget = normalizeForSearch(target, false);
  const tokens = normalizeForSearch(query, false).split(' ').filter(Boolean);
  return tokens.every((token) => {
    const hasSpecialChar = /[.+\-'']/.test(token);
    if (hasSpecialChar) {
      // Token has punctuation — must match in the raw (non-stripped) target
      return rawTarget.includes(token);
    }
    // Plain token — match against stripped target for maximum fuzziness
    return strippedTarget.includes(token);
  });
};

// Define the allowed banks
const ALLOWED_BANKS = [
  'Chase',
  'American Express',
  'Bank of America',
  'Citi',
] as const;

// Bank emoji
const BANK_EMOJI = '🏦';

// Bank search aliases
const BANK_SEARCH_TERMS: Record<string, string[]> = {
  Chase: ['chase', 'chase bank'],
  'American Express': ['american express', 'amex', 'americanexpress'],
  'Bank of America': ['bank of america', 'bofa', 'boa', 'bankofamerica'],
  Citi: ['citi', 'citibank', 'citi bank'],
};

type BankMatch = {
  bank: string;
  matchType: 'exact' | 'partial';
  matchScore: number;
};

/**
 * Matches a search query to bank names
 * Returns matches ordered by relevance
 */
function matchBanks(query: string): BankMatch[] {
  if (!query || query.length < 2) {
    return [];
  }

  const lowerQuery = query.toLowerCase().trim();
  const matches: BankMatch[] = [];

  ALLOWED_BANKS.forEach((bank) => {
    const lowerBank = bank.toLowerCase();
    const searchTerms = BANK_SEARCH_TERMS[bank] || [lowerBank];

    // Exact match on bank name
    if (lowerBank === lowerQuery) {
      matches.push({
        bank,
        matchType: 'exact',
        matchScore: 100,
      });
      return;
    }

    // Check search terms for exact match
    if (searchTerms.some((term) => term === lowerQuery)) {
      matches.push({
        bank,
        matchType: 'exact',
        matchScore: 95,
      });
      return;
    }

    // Partial match - bank name starts with query
    if (lowerBank.startsWith(lowerQuery)) {
      matches.push({
        bank,
        matchType: 'partial',
        matchScore: 80,
      });
      return;
    }

    // Partial match - search term starts with query
    if (searchTerms.some((term) => term.startsWith(lowerQuery))) {
      matches.push({
        bank,
        matchType: 'partial',
        matchScore: 70,
      });
      return;
    }

    // Contains match - bank name contains query
    if (lowerBank.includes(lowerQuery)) {
      matches.push({
        bank,
        matchType: 'partial',
        matchScore: 50,
      });
      return;
    }

    // Contains match - any search term contains query
    if (searchTerms.some((term) => term.includes(lowerQuery))) {
      matches.push({
        bank,
        matchType: 'partial',
        matchScore: 40,
      });
    }
  });

  // Sort by match score (highest first)
  return matches.sort((a, b) => b.matchScore - a.matchScore);
}

/**
 * Gets cards from a specific bank
 */
function getCardsForBank(bankName: string): CardData[] {
  return Object.values(CreditCardDataFiltered).filter(
    (card) => card.bank_name === bankName
  );
}

// Helper function to get earn rate for a card's category
const getCardEarnRateForCategory = (
  card: CardData,
  categoryKey: string
): { earnRate: number; formattedRate: string } | null => {
  if (!card.rewards_earn_rates || card.rewards_earn_rates.length === 0) {
    return null;
  }

  const flattenedRates = flattenRewardsEarnRates(
    card.rewards_earn_rates,
    [],
    card.rewardsType
  );

  // Find the earn rate for this category
  const matchingRate = flattenedRates.find(
    (rate) => rate.category === categoryKey
  );

  if (matchingRate) {
    return {
      earnRate: matchingRate.earnRate,
      formattedRate: formatEarnRate(matchingRate.earnRate, card.rewardsType),
    };
  }

  return null;
};

export const CreditCardSearchBar = ({
  handleAddCard,
  selectedCards,
  handleRemoveCard,
  setSelectedCards,
}: {
  handleAddCard: (card: CreditCard) => void;
  handleRemoveCard: (card: CreditCard) => void;
  selectedCards: ReadonlyArray<CreditCard>;
  setSelectedCards: Dispatch<SetStateAction<CreditCard[]>>;
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState<
    ReadonlyArray<CardData>
  >([]);
  const [selectedBank, setSelectedBank] = useState<string>('All cards');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [categoryMatches, setCategoryMatches] = useState<CategoryMatch[]>([]);
  const [selectedCategoryPills, setSelectedCategoryPills] = useState<
    CategoryMatch[]
  >([]);
  const [bankMatches, setBankMatches] = useState<BankMatch[]>([]);
  const [showRequestModal, setShowRequestModal] = useState(false);

  // Helper to get bank pill display info
  const getBankPillInfo = (bank: string) => {
    if (bank === 'All cards') {
      return null;
    }
    return { bank, label: bank, emoji: BANK_EMOJI };
  };

  const selectedBankPill = getBankPillInfo(selectedBank);
  const isFirstLoad = useRef(true);
  const screenSize = useScreenSize();
  const isMobile = screenSize === 'mobile';
  const inputRef = useRef<HTMLInputElement>(null);

  const filterValues = useCallback(
    (value: string) => {
      // Check if this is a category search
      const categoryMatchesResult = matchCategories(value);
      setCategoryMatches(categoryMatchesResult);

      // Check if this is a bank search
      const bankMatchesResult = matchBanks(value);
      setBankMatches(bankMatchesResult);

      // Get cards from selected category pills
      let categoryFilteredCards: CardData[] = [];
      if (selectedCategoryPills.length > 0) {
        const categoryCardsSet = new Set<string>();
        selectedCategoryPills.forEach((pill) => {
          const cards = getCardsForCategory(pill.categoryKey);
          cards.forEach((card) => categoryCardsSet.add(card.id));
        });
        categoryFilteredCards = Array.from(categoryCardsSet).map(
          (id) => CreditCardDataFiltered[id as CreditCard]
        );
      }

      // Always do regular card name/bank filtering
      const textFilteredCards = Object.values(CreditCardDataFiltered).filter(
        (card) =>
          fuzzyTokenMatch(card.name, value) ||
          (card.bank_name && fuzzyTokenMatch(card.bank_name, value)) ||
          fuzzyTokenMatch(
            `${card.bank_name ?? ''} ${card.name}`,
            value
          )
      );

      // If we have category pills, filter the text results to only show cards from those categories
      let filtered: CardData[];
      if (selectedCategoryPills.length > 0 && value) {
        // Show only cards that match both category pills AND text search
        filtered = categoryFilteredCards.filter((card) =>
          textFilteredCards.some((textCard) => textCard.id === card.id)
        );
      } else if (selectedCategoryPills.length > 0) {
        // Show all cards from category pills
        filtered = categoryFilteredCards;
      } else {
        // No category pills, just text search
        filtered = textFilteredCards;
      }

      // Apply bank filter if a specific bank is selected
      if (selectedBank !== 'All cards') {
        filtered = filtered.filter(
          (card) => card.bank_name && card.bank_name === selectedBank
        );
      }

      // Always sort alphabetically by name
      const sortedByName = filtered.sort((a, b) => {
        if (!a.name) {
          return -1;
        }
        if (!b.name) {
          return 1;
        }
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });

      // Only reorder selected cards to top on first load
      if (isFirstLoad.current) {
        return sortedByName.sort((a, b) => {
          if (selectedCards.includes(a.id) && !selectedCards.includes(b.id)) {
            return -1;
          }
          if (!selectedCards.includes(a.id) && selectedCards.includes(b.id)) {
            return 1;
          }
          return 0;
        });
      }

      return sortedByName;
    },
    [selectedCards, selectedBank, selectedCategoryPills]
  );

  // Mark first load as complete after initial render
  useEffect(() => {
    if (showSuggestions && isFirstLoad.current) {
      isFirstLoad.current = false;
    }
  }, [showSuggestions]);

  // Clear search value when search bar is closed, but keep pills
  useEffect(() => {
    if (!showSuggestions) {
      setSearchValue('');
      setCategoryMatches([]);
      setBankMatches([]);
    }
  }, [showSuggestions]);

  const handleAddCategoryPill = useCallback(
    (category: CategoryMatch) => {
      // Don't add if already exists
      if (
        selectedCategoryPills.some(
          (pill) => pill.categoryKey === category.categoryKey
        )
      ) {
        return;
      }
      setSelectedCategoryPills([...selectedCategoryPills, category]);
      setSearchValue('');
      setCategoryMatches([]);
      // Focus back on input
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [selectedCategoryPills]
  );

  const handleRemoveCategoryPill = useCallback(
    (categoryKey: string) => {
      setSelectedCategoryPills(
        selectedCategoryPills.filter((pill) => pill.categoryKey !== categoryKey)
      );
    },
    [selectedCategoryPills]
  );

  // Update filtered suggestions when bank filters change
  useEffect(() => {
    if (showSuggestions) {
      setFilteredSuggestions(filterValues(searchValue));
    }
  }, [selectedBank, filterValues, searchValue, showSuggestions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setFilteredSuggestions(filterValues(value));
  };

  const handleSuggestionClick = useCallback(
    (card: CreditCard) => {
      if (selectedCards.includes(card)) {
        handleRemoveCard(card);
      } else {
        handleAddCard(card);
      }
      setSearchValue('');
      setShowSuggestions(false);
    },
    [handleAddCard, handleRemoveCard, selectedCards]
  );

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 700,
        margin: 'auto',
        position: 'relative',
      }}
    >
      {showSuggestions && (
        // Invisible mask so clicking outside the suggestions closes them
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
          }}
          onClick={() => setShowSuggestions(false)}
        ></div>
      )}
      <style>{`
        @keyframes gradient-shift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        
        .gradient-border {
          padding: 1px;
          background: linear-gradient(
            90deg,
            #ff6b9d,
            #c471ed,
            #4da9e6,
            #1d72b8,
            #4da9e6,
            #c471ed,
            #ff6b9d
          );
          background-size: 300% 300%;
          border-radius: 100px;
          animation: gradient-shift 10s ease infinite;
        }
        
        .search-bar-container input::placeholder {
          font-size: 14px;
        }
      `}</style>
      <div className="gradient-border">
        <div
          className="search-bar-container"
          style={{
            padding: isMobile ? '12px 20px' : '9px 20px',
            boxSizing: 'border-box',
            width: '100%',
            borderRadius: 100,
            backgroundColor: '#fff',
            fontWeight: 400,
            zIndex: 10,
            boxShadow: '0 5px 20px rgba(0, 0, 0, 0.1)',
            border: 'none',
            outline: 'none',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 4,
          }}
          onClick={() => inputRef.current?.focus()}
        >
          {selectedCategoryPills.map((pill) => (
            <div
              key={pill.categoryKey}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                padding: '6px 9px',
                backgroundColor: '#E8F4FF',
                color: '#1d72b8',
                borderRadius: '14px',
                fontSize: '13px',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              <span>
                {pill.emoji} {pill.displayName}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveCategoryPill(pill.categoryKey);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  color: '#1d72b8',
                  fontSize: '14px',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '2px',
                }}
              >
                ×
              </button>
            </div>
          ))}
          {selectedBankPill && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                padding: '6px 9px',
                backgroundColor: '#FFF4E8',
                color: '#B8721D',
                borderRadius: '14px',
                fontSize: '13px',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              <span>
                {selectedBankPill.emoji} {selectedBankPill.label}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBank('All cards');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  color: '#B8721D',
                  fontSize: '14px',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '2px',
                }}
              >
                ×
              </button>
            </div>
          )}
          <input
            ref={inputRef}
            onFocus={(e) => {
              setShowSuggestions(true);
              setFilteredSuggestions(filterValues(searchValue));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setShowSuggestions(false);
                inputRef.current?.blur();
                return;
              }
              if (e.key === 'Backspace' && searchValue === '') {
                // If bank pill exists, remove it first
                if (selectedBankPill) {
                  setSelectedBank('All cards');
                  return;
                }
                // Otherwise remove the last category pill
                if (selectedCategoryPills.length > 0) {
                  const newPills = [...selectedCategoryPills];
                  newPills.pop();
                  setSelectedCategoryPills(newPills);
                }
              }
            }}
            style={{
              flex: 1,
              minWidth: '120px',
              border: 'none',
              outline: 'none',
              minHeight: isMobile ? '26px' : '30px',
              fontWeight: 400,
              fontSize: '14px',
              backgroundColor: 'transparent',
            }}
            type="text"
            value={searchValue}
            onChange={handleInputChange}
            placeholder={'Search for a card or category'}
          />
        </div>
      </div>
      {showSuggestions && (
        <ul
          style={{
            listStyle: 'none',
            boxSizing: 'border-box',
            width: '100%',
            padding: '0 20px 20px 20px',
            position: 'absolute',
            marginTop: '5px',
            backgroundColor: '#fff',
            borderRadius: 30,
            boxShadow: '0 20px 30px rgba(0, 0, 0, 0.25)',
            zIndex: 1001,
            maxHeight: '80vh',
            overflowY: 'auto',
            left: 0,
            right: 0,
            minWidth: 0, // Prevents overflow on mobile
          }}
        >
          <div
            style={{
              position: 'sticky',
              paddingTop: '20px',
              paddingBottom: '6px',
              top: 0,
              backgroundColor: '#fff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}
          >
            {/* Selection Summary */}
            <SegmentedControl
              options={[
                { value: 'All cards', label: 'All cards' },
                ...ALLOWED_BANKS.map((bank) => ({ value: bank, label: bank })),
              ]}
              value={selectedBank}
              onChange={(value) => {
                setSelectedBank(value);
                // Focus input after bank selection
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
            />
          </div>

          {/* Category-based suggestions */}
          {categoryMatches.length > 0 &&
            searchValue.length >= 2 &&
            (() => {
              const topMatch = categoryMatches[0];

              // Don't show if category is already added as a pill
              if (
                selectedCategoryPills.some(
                  (pill) => pill.categoryKey === topMatch.categoryKey
                )
              ) {
                return null;
              }

              const categoryCards = getCardsForCategory(
                topMatch.categoryKey
              ).slice(0, 3);

              if (categoryCards.length === 0) {
                return null;
              }

              return (
                <div style={{ marginBottom: '10px' }}>
                  <div
                    style={{
                      padding: '10px 10px 5px 10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#888',
                      }}
                    >
                      Cards earning on {topMatch.emoji} {topMatch.displayName}
                    </span>
                    <button
                      onClick={() => {
                        handleAddCategoryPill(topMatch);
                      }}
                      style={{
                        padding: '4px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: '#4DA9E6',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}
                    >
                      Add as filter
                    </button>
                  </div>
                  {categoryCards.map((card) => {
                    const earnRateInfo = getCardEarnRateForCategory(
                      card,
                      topMatch.categoryKey
                    );
                    return (
                      <button
                        key={card.id}
                        style={{
                          border: 'none',
                          background: 'none',
                          display: 'block',
                          fontSize: 'inherit',
                          width: '100%',
                        }}
                        onClick={() => handleSuggestionClick(card.id)}
                      >
                        <li
                          style={{
                            display: 'flex',
                            padding: '5px 10px',
                            borderRadius: 10,
                            flexDirection: 'row',
                            ...(selectedCards.includes(card.id) && {
                              backgroundColor: '#f0f0f0',
                            }),
                            textAlign: 'left',
                            alignItems: 'center',
                            gap: 10,
                            fontWeight: selectedCards.includes(card.id)
                              ? 600
                              : 400,
                            color: selectedCards.includes(card.id)
                              ? '#000'
                              : '#6488A3',
                          }}
                        >
                          <span style={{ marginRight: 8 }}>
                            {selectedCards.includes(card.id) ? (
                              <FontAwesomeIcon icon={faCheck} />
                            ) : (
                              <div style={{ width: 16 }} />
                            )}
                          </span>
                          <CreditCardImage
                            imageName={card.imageName}
                            alt={card.name}
                            width={60}
                          />
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                            }}
                          >
                            <div>{card.name}</div>
                            {selectedCategoryPills.length > 0 &&
                              earnRateInfo && (
                                <div
                                  style={{
                                    fontSize: '12px',
                                    color: '#4DA9E6',
                                    fontWeight: 500,
                                  }}
                                >
                                  {earnRateInfo.formattedRate} on{' '}
                                  {formatSpendCategory(topMatch.categoryKey)}
                                </div>
                              )}
                          </div>
                        </li>
                      </button>
                    );
                  })}
                </div>
              );
            })()}

          {/* Bank-based suggestions */}
          {bankMatches.length > 0 &&
            searchValue.length >= 2 &&
            (() => {
              const topBankMatch = bankMatches[0];

              // Don't show if bank is already selected
              if (selectedBank === topBankMatch.bank) {
                return null;
              }

              const bankCards = getCardsForBank(topBankMatch.bank).slice(0, 3);

              if (bankCards.length === 0) {
                return null;
              }

              return (
                <div style={{ marginBottom: '10px' }}>
                  <div
                    style={{
                      padding: '10px 10px 5px 10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#888',
                      }}
                    >
                      {BANK_EMOJI} {topBankMatch.bank} cards
                    </span>
                    <button
                      onClick={() => {
                        setSelectedBank(topBankMatch.bank);
                        setSearchValue('');
                        setTimeout(() => inputRef.current?.focus(), 0);
                      }}
                      style={{
                        padding: '4px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: '#4DA9E6',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}
                    >
                      Show only {topBankMatch.bank} cards
                    </button>
                  </div>
                  {bankCards.map((card) => {
                    return (
                      <button
                        key={card.id}
                        style={{
                          border: 'none',
                          background: 'none',
                          display: 'block',
                          fontSize: 'inherit',
                          width: '100%',
                        }}
                        onClick={() => handleSuggestionClick(card.id)}
                      >
                        <li
                          style={{
                            display: 'flex',
                            padding: '5px 10px',
                            borderRadius: 10,
                            flexDirection: 'row',
                            ...(selectedCards.includes(card.id) && {
                              backgroundColor: '#f0f0f0',
                            }),
                            textAlign: 'left',
                            alignItems: 'center',
                            gap: 10,
                            fontWeight: selectedCards.includes(card.id)
                              ? 600
                              : 400,
                            color: selectedCards.includes(card.id)
                              ? '#000'
                              : '#6488A3',
                          }}
                        >
                          <span style={{ marginRight: 8 }}>
                            {selectedCards.includes(card.id) ? (
                              <FontAwesomeIcon icon={faCheck} />
                            ) : (
                              <div style={{ width: 16 }} />
                            )}
                          </span>
                          <CreditCardImage
                            imageName={card.imageName}
                            alt={card.name}
                            width={60}
                          />
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                            }}
                          >
                            <div>{card.name}</div>
                          </div>
                        </li>
                      </button>
                    );
                  })}
                </div>
              );
            })()}

          {/* Filtering header when category pills are active */}
          {selectedCategoryPills.length > 0 && (
            <div
              style={{
                padding: '10px 10px 5px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              <span style={{ color: '#888', lineHeight: 1.5 }}>
                Filtering by{' '}
                {selectedCategoryPills.map((pill, index) => (
                  <span key={pill.categoryKey}>
                    {pill.emoji} {pill.displayName}
                    {index < selectedCategoryPills.length - 1 && ' and '}
                  </span>
                ))}
              </span>
              <button
                onClick={() => setSelectedCategoryPills([])}
                style={{
                  padding: '4px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: '#4DA9E6',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  lineHeight: 1,
                }}
              >
                Clear filter
              </button>
            </div>
          )}

          {/* Regular card search results */}
          {filteredSuggestions.length > 0 && searchValue && (
            <div
              style={{
                padding: '10px 10px 5px 10px',
                fontWeight: 500,
                fontSize: '14px',
                color: '#888',
              }}
            >
              Cards matching "{searchValue}"
            </div>
          )}

          {filteredSuggestions.map((card) => {
            // Get earn rates for all selected category pills
            const earnRates =
              selectedCategoryPills.length > 0
                ? selectedCategoryPills.reduce<
                    Array<{
                      pill: CategoryMatch;
                      rateInfo: { earnRate: number; formattedRate: string };
                    }>
                  >((acc, pill) => {
                    const rateInfo = getCardEarnRateForCategory(
                      card,
                      pill.categoryKey
                    );
                    if (rateInfo) {
                      acc.push({ pill, rateInfo });
                    }
                    return acc;
                  }, [])
                : [];

            return (
              <button
                key={card.id}
                style={{
                  border: 'none',
                  background: 'none',
                  display: 'block',
                  fontSize: 'inherit',
                  width: '100%',
                }}
                onClick={() => handleSuggestionClick(card.id)}
              >
                <li
                  style={{
                    display: 'flex',
                    padding: '5px 10px',
                    borderRadius: 10,
                    flexDirection: 'row',
                    ...(selectedCards.includes(card.id) && {
                      backgroundColor: '#f0f0f0',
                    }),
                    textAlign: 'left',
                    alignItems: 'center',
                    gap: 10,
                    fontWeight: selectedCards.includes(card.id) ? 600 : 400,
                    color: selectedCards.includes(card.id) ? '#000' : '#6488A3',
                  }}
                >
                  <span style={{ marginRight: 8 }}>
                    {selectedCards.includes(card.id) ? (
                      <FontAwesomeIcon icon={faCheck} />
                    ) : (
                      <div style={{ width: 16 }} />
                    )}
                  </span>
                  <CreditCardImage
                    imageName={card.imageName}
                    alt={card.name}
                    width={60}
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <div>{card.name}</div>
                    {earnRates.length > 0 && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#4DA9E6',
                          fontWeight: 500,
                        }}
                      >
                        {earnRates.map((item, index) => (
                          <span key={item.pill.categoryKey}>
                            {item.rateInfo.formattedRate} on{' '}
                            {formatSpendCategory(item.pill.categoryKey)}
                            {index < earnRates.length - 1 && ', '}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              </button>
            );
          })}
          {filteredSuggestions.length === 0 && (
            <li style={{ padding: '5px', color: '#999' }}>
              {searchValue || selectedBank !== 'All cards' ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p style={{ margin: '0 0 15px 0', color: '#666' }}>
                    No cards found matching your search
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        setSearchValue('');
                        setSelectedBank('All cards');
                        setSelectedCategoryPills([]);
                        setTimeout(() => inputRef.current?.focus(), 0);
                      }}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '20px',
                        border: '1px solid #ddd',
                        backgroundColor: 'white',
                        color: '#666',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      Clear filters
                    </button>
                    <button
                      onClick={() => setShowRequestModal(true)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '20px',
                        border: 'none',
                        backgroundColor: '#4DA9E6',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      Request "{searchValue}"
                    </button>
                  </div>
                </div>
              ) : (
                'No suggestions'
              )}
            </li>
          )}
        </ul>
      )}
      {showRequestModal && (
        <RequestCardModal
          onClose={() => setShowRequestModal(false)}
          initialCardName={searchValue}
        />
      )}
    </div>
  );
};
